<template>
  <div class="survey-page animate-fade-in-up">
    <div v-if="loading" class="survey-loading">
      <i class="el-icon-loading"></i> 加载问卷中...
    </div>

    <template v-else-if="surveyData">
      <div class="survey-header">
        <div class="survey-header-icon">
          <i class="el-icon-document-checked"></i>
        </div>
        <h1 class="survey-title">员工离职调研问卷</h1>
        <p class="survey-subtitle">您的反馈对我们至关重要，所有信息仅用于管理改进</p>
      </div>

      <div class="survey-card">
        <div class="survey-intro">
          {{ templateText }}
        </div>

        <div
          v-for="(q, idx) in questions"
          :key="q.question_id"
          class="survey-section"
          :data-qid="q.question_id"
        >
          <h3 class="section-title">
            <span class="section-number">{{ idx + 1 }}</span>
            {{ q.question_text }}
            <span v-if="q.required" class="section-required">*</span>
            <span v-if="q.question_type === 'multiple_choice' && q.max_select" class="section-limit">最多选{{ q.max_select }}项</span>
            <span v-if="!q.required" class="section-optional">选填</span>
          </h3>

          <!-- 单选题 -->
          <template v-if="q.question_type === 'single_choice'">
            <el-radio-group v-model="answers[q.question_id]" class="choice-group">
              <div v-for="(opt, i) in q.options" :key="i" class="choice-item">
                <el-radio :label="opt">{{ opt }}</el-radio>
              </div>
            </el-radio-group>
          </template>

          <!-- 多选题 -->
          <template v-else-if="q.question_type === 'multiple_choice'">
            <el-checkbox-group
              v-model="answers[q.question_id]"
              :max="q.max_select || undefined"
              class="choice-group"
            >
              <div v-for="(opt, i) in q.options" :key="i" class="choice-item">
                <el-checkbox :label="opt">{{ opt }}</el-checkbox>
              </div>
            </el-checkbox-group>
          </template>

          <!-- 填空题 -->
          <template v-else-if="q.question_type === 'fill_blank'">
            <el-input
              type="textarea"
              v-model="answers[q.question_id]"
              :rows="5"
              maxlength="1000"
              show-word-limit
              placeholder="请输入您的回答..."
              class="feedback-textarea"
            />
          </template>
        </div>

        <el-button
          type="primary"
          size="large"
          class="submit-btn btn-click"
          :loading="submitting"
          @click="submitSurvey"
        >
          <i class="el-icon-check"></i>
          {{ submitting ? '数据处理中...' : '提交调研反馈' }}
        </el-button>
      </div>
    </template>

    <div v-else class="survey-error">
      <i class="el-icon-warning-outline"></i>
      <p>问卷加载失败，请检查链接是否有效</p>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SurveyFill',
  data() {
    return {
      loading: true,
      surveyData: null,
      questions: [],
      answers: {},
      templateText: '感谢您抽出时间来做这个调研。因为您是我们的绩优员工，我们很想了解您的真实想法。',
      submitting: false
    }
  },
  computed: {
    token() {
      return this.$route.params.token || this.$route.query.token || ''
    }
  },
  created() {
    this.loadSurvey()
  },
  methods: {
    async loadSurvey() {
      if (!this.token) {
        this.loading = false
        return
      }
      try {
        // 先获取问卷基本信息
        const res = await this.$api.getSurveyDetail(this.token)
        if (res.code === 200) {
          this.surveyData = res.data
          // 再获取题目列表
          const qRes = await this.$api.fetchSurveyQuestionsPublic(this.token)
          if (qRes.code === 200) {
            this.questions = qRes.data.questions || []
            // 初始化答案对象
            this.questions.forEach(q => {
              if (q.question_type === 'multiple_choice') {
                this.$set(this.answers, q.question_id, [])
              } else {
                this.$set(this.answers, q.question_id, '')
              }
            })
          }
        }
      } catch (e) {
        console.error('加载问卷失败', e)
      } finally {
        this.loading = false
      }
    },
    async submitSurvey() {
      // 校验必填
      for (const q of this.questions) {
        if (q.required) {
          const ans = this.answers[q.question_id]
          let invalid = false
          if (q.question_type === 'multiple_choice') {
            if (!ans || ans.length === 0) invalid = true
          } else {
            if (!ans || (typeof ans === 'string' && ans.trim() === '')) invalid = true
          }
          if (invalid) {
            this.$message.warning(`请回答第 ${q.question_id} 题：${q.question_text}`)
            // 滚动到未填写的题目
            const el = document.querySelector(`[data-qid="${q.question_id}"]`)
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
            return
          }
        }
      }

      this.submitting = true

      // 构造答案提交
      const answerPayload = {}
      for (const q of this.questions) {
        const ans = this.answers[q.question_id]
        if (q.question_type === 'multiple_choice') {
          answerPayload[q.question_id] = ans || []
        } else {
          answerPayload[q.question_id] = ans || ''
        }
      }

      // 更新 survey 状态
      try {
        await this.$api.submitSurveyResponse(this.token, { answers: answerPayload })
      } catch (e) {
        this.$message.error(e.message || '提交失败')
        this.submitting = false
        return
      }

      const surveys = this.$store.state.surveys.surveys || []
      const survey = surveys.find(s => s.token === this.token)

      const response = {
        response_id: Date.now(),
        survey_id: survey ? survey.survey_id : null,
        employee_id: survey ? survey.employee_id : null,
        employee_name: survey ? survey.employee_name : '未知员工',
        answers: answerPayload,
        submitted_at: new Date().toISOString().replace('T', ' ').slice(0, 19),
        task_id: survey ? survey.task_id : 1
      }

      if (survey) {
        survey.status = 'submitted'
        this.$store.commit('SET_SURVEYS', [...surveys])
      }

      this.$store.commit('ADD_RESPONSE', response)

      setTimeout(() => {
        this.$message.success('您的反馈已成功记录，感谢您的参与，祝您未来一切顺利。')
        this.submitting = false
      }, 800)
    }
  }
}
</script>

<style scoped>
.survey-page {
  max-width: 720px;
  margin: 0 auto;
  padding: var(--space-3xl) var(--space-xl);
  background: var(--color-bg);
  min-height: 100vh;
}

.survey-loading {
  text-align: center;
  padding: var(--space-3xl);
  color: var(--color-text-secondary);
  font-size: 16px;
}

.survey-loading i {
  font-size: 32px;
  margin-bottom: var(--space-md);
  display: block;
}

.survey-error {
  text-align: center;
  padding: var(--space-3xl);
  color: var(--color-high);
  font-size: 16px;
}

.survey-error i {
  font-size: 48px;
  margin-bottom: var(--space-md);
  display: block;
}

.survey-header {
  text-align: center;
  margin-bottom: var(--space-2xl);
}

.survey-header-icon {
  width: 64px;
  height: 64px;
  border-radius: var(--radius-full);
  background: var(--color-primary-gradient);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  margin: 0 auto var(--space-lg);
}

.survey-title {
  font-size: 24px;
  font-weight: 700;
  color: var(--color-text);
  margin: 0 0 var(--space-sm) 0;
}

.survey-subtitle {
  font-size: 14px;
  color: var(--color-text-secondary);
  margin: 0;
}

.survey-card {
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  padding: var(--space-2xl);
  box-shadow: var(--shadow-sm);
}

.survey-intro {
  padding: var(--space-lg);
  background: var(--color-bg);
  border-radius: var(--radius-md);
  font-size: 14px;
  color: var(--color-text-secondary);
  line-height: 1.6;
  margin-bottom: var(--space-2xl);
}

.survey-section {
  margin-bottom: var(--space-2xl);
}

.survey-section:last-of-type {
  margin-bottom: var(--space-xl);
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  color: var(--color-text);
  margin: 0 0 var(--space-md) 0;
  display: flex;
  align-items: center;
  gap: var(--space-sm);
}

.section-number {
  width: 28px;
  height: 28px;
  border-radius: var(--radius-full);
  background: var(--color-primary-gradient);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  flex-shrink: 0;
}

.section-required {
  color: var(--color-high);
  font-size: 16px;
}

.section-limit,
.section-optional {
  margin-left: auto;
  font-size: 12px;
  font-weight: 400;
  color: var(--color-text-muted);
  background: var(--color-bg);
  padding: 2px 10px;
  border-radius: var(--radius-full);
}

.choice-group {
  display: flex;
  flex-direction: column;
  gap: var(--space-sm);
}

.choice-item {
  padding: var(--space-sm) var(--space-md);
  background: var(--color-bg);
  border-radius: var(--radius-md);
  transition: all var(--duration-fast) var(--ease-out);
}

.choice-item:hover {
  background: var(--color-border-light);
}

.feedback-textarea {
  margin-top: var(--space-sm);
}

.submit-btn {
  width: 100%;
  height: 48px;
  font-size: 16px;
  font-weight: 600;
}
</style>
